import api from './api';

export interface Splitter {
  id: number;
  caixa_id?: number | null;
  armario_id?: number | null;
  tipo: string;
  posicao_x: number;
  posicao_y: number;
  config_saidas: number;
  createdAt: string;
  updatedAt: string;
}

export interface SplitterConnection {
  id: number;
  caixa_id?: number | null;
  armario_id?: number | null;
  source: string; // Ex: 'splitter-54', 'cabo-12'
  sourceHandle?: string; // Ex: 'output-0', 'output-1'
  target: string; // Ex: 'splitter-55', 'cabo-13'
  targetHandle?: string; // Ex: 'input-0', 'output-fo1'
  connection_label?: string;
  style_color?: string;
  style_width?: number;
}

export interface ReactFlowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: {
    id: number;
    label: string;
    tipo: string;
    config: string;
    color: string;
    inputs: number;
    outputs: number;
  };
}

export interface ReactFlowEdge {
  id: string;
  source: string;
  sourceHandle?: string;
  target: string;
  targetHandle?: string;
  type: string;
  animated: boolean;
  label: string;
  style: {
    stroke: string;
    strokeWidth: number;
  };
  labelStyle: {
    fontSize: number;
    fontWeight: string;
  };
  data: {
    id: number;
    caixa_id: number;
    onDelete?: (edgeId: string) => void;
  };
}

class SplitterService {
  // Buscar splitters de uma CAIXA
  async getSplittersByCaixa(caixaId: number): Promise<Splitter[]> {
    try {
      const response = await api.get(`/splitters/caixa/${caixaId}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro ao buscar splitters:', error);
      throw error;
    }
  }

  // Buscar nós formatados para React Flow
  async getNodesForReactFlow(caixaId: number): Promise<{ nodes: ReactFlowNode[]; caixa: any }> {
    try {
      const response = await api.get(`/splitters/caixa/${caixaId}/nodes`);

      // Validação defensiva da resposta
      if (!response.data) {
        console.warn('⚠️ Resposta sem data, retornando estrutura padrão');
        return { nodes: [], caixa: null };
      }

      const data = response.data.data || response.data;

      // Garantir que a estrutura está correta
      if (!data || typeof data !== 'object') {
        console.warn('⚠️ Data inválido, retornando estrutura padrão');
        return { nodes: [], caixa: null };
      }

      // Retornar com valores padrão se necessário
      return {
        nodes: Array.isArray(data.nodes) ? data.nodes : [],
        caixa: data.caixa || null
      };

    } catch (error) {
      console.error('Erro ao buscar nós para React Flow:', error);
      // Retornar estrutura padrão em caso de erro
      return { nodes: [], caixa: null };
    }
  }

  // Criar novo splitter
  async createSplitter(splitterData: Partial<Splitter>): Promise<Splitter> {
    try {
      console.log('🔄 Criando splitter com dados:', splitterData);

      const response = await api.post('/splitters', splitterData);

      console.log('📡 Response completo (createSplitter):', response);
      console.log('📡 Response type:', typeof response);

      // O interceptor já processou, então response é o data
      const data = response as any;
      console.log('📊 Data processado:', data);

      // Se temos data.data, usar isso (estrutura padrão)
      if (data.data && typeof data.data === 'object') {
        console.log('✅ Usando data.data:', data.data);
        console.log('🔍 config_saidas retornado:', data.data.config_saidas, 'tipo:', typeof data.data.config_saidas);
        return data.data;
      }

      // Se data já é o objeto splitter
      if (data.id) {
        console.log('✅ Usando data diretamente:', data);
        console.log('🔍 config_saidas retornado:', data.config_saidas, 'tipo:', typeof data.config_saidas);
        return data;
      }

      console.error('❌ Estrutura de resposta não reconhecida');
      console.error('📊 Estrutura completa:', JSON.stringify(data, null, 2));
      throw new Error('Resposta inválida do servidor');

    } catch (error) {
      console.error('Erro ao criar splitter:', error);
      throw error;
    }
  }

  // Atualizar splitter
  async updateSplitter(id: number, splitterData: Partial<Splitter>): Promise<Splitter> {
    try {
      const response = await api.put(`/splitters/${id}`, splitterData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro ao atualizar splitter:', error);
      throw error;
    }
  }

  // Atualizar posição do splitter
  async updateSplitterPosition(id: number, position: { x: number; y: number }): Promise<void> {
    try {
      await api.put(`/splitters/${id}/position`, {
        posicao_x: position.x,
        posicao_y: position.y
      });
    } catch (error) {
      console.error('Erro ao atualizar posição do splitter:', error);
      throw error;
    }
  }

  // Atualizar observação do splitter
  async updateSplitterObservacao(id: number, observacao: string): Promise<Splitter> {
    try {
      console.log(`🔄 Atualizando observação do splitter ${id}:`, observacao);

      const response = await api.put(`/splitters/${id}/observacao`, {
        observacao: observacao
      });

      console.log('📡 Response completo (updateSplitterObservacao):', response);

      // O interceptor retorna response.data, então response já é o data do backend
      const data = response as any;
      console.log('📡 Data para processar:', data);

      // Validar se temos dados
      if (!data) {
        console.error('❌ Dados são undefined/null');
        throw new Error('Nenhum dado retornado pela API');
      }

      // Se data tem success=false, é um erro do backend
      if (data.success === false) {
        console.error('❌ Backend retornou success=false:', data.message);
        throw new Error(data.message || 'Erro do servidor');
      }

      // Tentar extrair dados do splitter atualizado
      let splitterData: any = data;

      // Se tem data.data, usar isso
      if (data.data && typeof data.data === 'object') {
        splitterData = data.data;
      }

      console.log('✅ Observação atualizada com sucesso:', splitterData);
      return splitterData;

    } catch (error) {
      console.error('❌ Erro ao atualizar observação do splitter:', error);
      throw error;
    }
  }

  // Atualizar porcentagem da saída 1 (apenas splitters 1x2)
  async updateSaida1Porcentagem(id: number, porcentagem: number): Promise<{
    saida1_porcentagem: number,
    saida2_porcentagem: number,
    config_json: any
  }> {
    try {
      console.log(`🔄 Chamando API para atualizar splitter ${id} com ${porcentagem}%`);

      const response = await api.put(`/splitters/${id}/porcentagem`, {
        saida1_porcentagem: porcentagem
      });

      console.log('📡 Response completo:', response);

      // O interceptor retorna response.data, então response já é o data do backend
      const data = response as any; // Cast necessário devido ao interceptor
      console.log('📡 Data para processar:', data);

      // Validar se temos dados
      if (!data) {
        console.error('❌ Dados são undefined/null');
        throw new Error('Nenhum dado retornado pela API');
      }

      // Se data tem success=false, é um erro do backend
      if (data.success === false) {
        console.error('❌ Backend retornou success=false:', data.message);
        throw new Error(data.message || 'Erro do servidor');
      }

      // Se data tem as propriedades que esperamos diretamente (id, saida1_porcentagem)
      if (data.id && data.saida1_porcentagem !== undefined) {
        console.log('✅ Dados válidos recebidos diretamente:', data);
        return {
          saida1_porcentagem: data.saida1_porcentagem,
          saida2_porcentagem: data.saida2_porcentagem,
          config_json: data.config_json
        };
      }

      // Se temos data.data (estrutura padrão da API)
      if (data.data && typeof data.data === 'object') {
        console.log('✅ Dados válidos recebidos em data.data:', data.data);
        return {
          saida1_porcentagem: data.data.saida1_porcentagem,
          saida2_porcentagem: data.data.saida2_porcentagem,
          config_json: data.data.config_json
        };
      }

      // Se chegou até aqui, não conseguimos entender a estrutura
      console.error('❌ Estrutura de resposta não reconhecida');
      console.error('📊 Estrutura completa:', JSON.stringify(data, null, 2));
      throw new Error('Estrutura de resposta não reconhecida');

    } catch (error: any) {
      console.error('❌ Erro ao atualizar porcentagem do splitter:');
      console.error('❌ Error object:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);

      // Se é um erro de rede/axios
      if (error.response) {
        console.error('❌ Response status:', error.response.status);
        console.error('❌ Response headers:', error.response.headers);
        console.error('❌ Response data:', error.response.data);
      } else if (error.request) {
        console.error('❌ Request foi feito mas sem resposta:', error.request);
      } else {
        console.error('❌ Erro ao configurar request:', error.message);
      }

      throw error;
    }
  }

  // Atualizar tipo de uma porta (caboin/caboout): in ou out
  async updatePortaTipo(splitterId: number, numeroFibra: number, tipo: 'in' | 'out'): Promise<{ portas_tipo: Record<string, 'in' | 'out'> }> {
    const response = await api.put(`/splitters/${splitterId}/porta`, { numeroFibra, tipo }) as any;
    if (response?.data?.portas_tipo) return { portas_tipo: response.data.portas_tipo };
    if (response?.portas_tipo) return { portas_tipo: response.portas_tipo };
    throw new Error('Resposta inválida ao atualizar tipo da porta');
  }

  // Buscar siblings de cabo (outros nós caboIn/caboOut do mesmo cabo na mesma caixa/armário)
  async getCaboSiblings(id: number): Promise<{ hasSiblings: boolean; siblingIds: number[] }> {
    try {
      const response = await api.get(`/splitters/${id}/cabo-siblings`);
      const data = response as any;
      return {
        hasSiblings: data?.hasSiblings ?? false,
        siblingIds: Array.isArray(data?.siblingIds) ? data.siblingIds : []
      };
    } catch (error) {
      console.error('Erro ao buscar siblings:', error);
      return { hasSiblings: false, siblingIds: [] };
    }
  }

  // Deletar splitter
  async deleteSplitter(id: number, deletePair = false): Promise<void> {
    try {
      const params = deletePair ? { deletePair: 'true' } : undefined;
      await api.delete(`/splitters/${id}`, { params });
    } catch (error) {
      console.error('Erro ao deletar splitter:', error);
      throw error;
    }
  }

  // Buscar conexões de uma CAIXA
  async getConnectionsByCaixa(caixaId: number): Promise<SplitterConnection[]> {
    try {
      const response = await api.get(`/splitter-connections/caixa/${caixaId}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro ao buscar conexões:', error);
      throw error;
    }
  }

  // Buscar nós formatados para React Flow - ARMÁRIO
  async getNodesForReactFlowArmario(armarioId: number): Promise<{ nodes: ReactFlowNode[]; armario: any }> {
    try {
      const response = await api.get(`/splitters/armario/${armarioId}/nodes`);
      const data = response.data.data || response.data;
      if (!data || typeof data !== 'object') {
        return { nodes: [], armario: null };
      }
      return {
        nodes: Array.isArray(data.nodes) ? data.nodes : [],
        armario: data.armario || null
      };
    } catch (error) {
      console.error('Erro ao buscar nós do armário:', error);
      return { nodes: [], armario: null };
    }
  }

  // Buscar edges formatados para React Flow - ARMÁRIO
  async getEdgesForReactFlowArmario(armarioId: number): Promise<ReactFlowEdge[]> {
    try {
      const response = await api.get(`/splitter-connections/armario/${armarioId}/edges`);
      const data = response.data.data || response.data;
      if (!Array.isArray(data)) return [];
      return data;
    } catch (error) {
      console.error('Erro ao buscar edges do armário:', error);
      return [];
    }
  }

  // Buscar edges formatados para React Flow
  async getEdgesForReactFlow(caixaId: number): Promise<ReactFlowEdge[]> {
    try {
      const response = await api.get(`/splitter-connections/caixa/${caixaId}/edges`);

      // Validação defensiva da resposta
      if (!response.data) {
        console.warn('⚠️ Resposta sem data para edges, retornando array vazio');
        return [];
      }

      const data = response.data.data || response.data;

      // Garantir que é um array
      if (!Array.isArray(data)) {
        console.warn('⚠️ Data não é array para edges, retornando array vazio');
        return [];
      }

      return data;

    } catch (error) {
      console.error('Erro ao buscar edges para React Flow:', error);
      // Retornar array vazio em caso de erro
      return [];
    }
  }

  // Criar nova conexão
  async createConnection(connectionData: Partial<SplitterConnection>): Promise<SplitterConnection> {
    try {
      const response = await api.post('/splitter-connections', connectionData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro ao criar conexão:', error);
      throw error;
    }
  }

  // Atualizar conexão
  async updateConnection(id: number, connectionData: Partial<SplitterConnection>): Promise<SplitterConnection> {
    try {
      const response = await api.put(`/splitter-connections/${id}`, connectionData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro ao atualizar conexão:', error);
      throw error;
    }
  }

  // Deletar conexão
  async deleteConnection(id: number): Promise<void> {
    try {
      await api.delete(`/splitter-connections/${id}`);
    } catch (error) {
      console.error('Erro ao deletar conexão:', error);
      throw error;
    }
  }

  // Buscar sinais ópticos calculados (usado no diagrama para animar cabos com sinal)
  async getOpticalSignals(id: number, type: 'caixa' | 'armario'): Promise<any> {
    try {
      console.log('[splitterService.getOpticalSignals] 📡 Request:', { sourceId: id, sourceType: type });
      const response = await api.get(`/optical/calculate/network`, {
        params: { sourceId: id, sourceType: type }
      });
      const data = response as any;
      const result = data.data ?? data;
      const keys = result && typeof result === 'object' ? Object.keys(result) : [];
      console.log('[splitterService.getOpticalSignals] 📥 Response:', {
        hasData: !!data,
        hasDataData: !!(data && data.data),
        resultKeysCount: keys.length,
        resultKeysSample: keys.slice(0, 5),
        firstNodeSample: keys.length > 0 && result[keys[0]] ? { outputSignals: result[keys[0]].outputSignals } : null
      });
      return result ?? {};
    } catch (error) {
      console.error('[splitterService.getOpticalSignals] ❌ Erro ao buscar sinais ópticos:', error);
      return {};
    }
  }
}

export const splitterService = new SplitterService();
export default splitterService; 